import { useState, useRef, useEffect } from 'react';
import { Layout } from 'components/Layout';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from 'components/ui/pagination';
import { Play, Plus, X } from 'lucide-react';
import { useToast } from 'hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { Info } from "lucide-react";
import axios, { apiUrlForPath } from 'utils/axiosConfig';

// Types for API responses
type Language = {
  id: string;
  name: string;
  tags: string[];
  url: string;
  category?: string;
  description?: string;
  elevenlabs_key_id?: number;
};

type LanguageResponse = {
  data: Language[];
  total: number;
  page: number;
  totalPages: number;
  nextPageToken?: string;
};

const LanguagePage = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLanguages, setSelectedLanguages] = useState<Language[]>([]);
  const [selectedTestLanguage, setSelectedTestLanguage] = useState('');
  const [testText, setTestText] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [generatedAudioSrc, setGeneratedAudioSrc] = useState('');
  const audioRef = useRef<HTMLAudioElement>(null);
  const [showInfo, setShowInfo] = useState(true);
  
  // State for API data
  const [languages, setLanguages] = useState<Language[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const maxLanguages = 10;

  // Fetch languages when page or search term changes
  useEffect(() => {
    fetchLanguages();
  }, [currentPage, searchTerm]);

  // Fetch selected languages on initial load
  useEffect(() => {
    fetchSelectedLanguages();
  }, []);

  useEffect(() => {
    if (selectedLanguages.length > 1 && showInfo) {
      const t = setTimeout(() => setShowInfo(false), 500);
      return () => clearTimeout(t);
    }
  }, [selectedLanguages, showInfo]);

  // Fetch languages with pagination and search
  const fetchLanguages = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get<LanguageResponse>('/v2/languages', {
        params: {
          page: currentPage,
          search: searchTerm || undefined
        }
      });
      
      setLanguages(response.data.data);
      setTotalPages(response.data.totalPages ?? 1);
    } catch (err) {
      console.error('Error fetching languages:', err);
      setError('Failed to load languages. Please try again.');
      toast({
        title: "Error",
        description: "Failed to load languages. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's selected languages
  const fetchSelectedLanguages = async () => {
    try {
      const response = await axios.get<Language[]>('/v2/user/languages');
      setSelectedLanguages(response.data);
    } catch (err) {
      console.error('Error fetching selected languages:', err);
      toast({
        title: "Error",
        description: "Failed to load your selected languages.",
        variant: "destructive"
      });
    }
  };

  // Handle adding a language
  const handleAddLanguage = async (language: Language) => {
    if (selectedLanguages.length >= maxLanguages) {
      toast({
        title: "Maximum languages reached",
        description: "Please remove a language before adding another one.",
        variant: "destructive"
      });
      return;
    }
    
    if (!selectedLanguages.some(lang => lang.id === language.id)) {
      try {
        await axios.post('/v2/user/languages', { 
          languageId: language.id,
          elevenlabsKeyId: language.elevenlabs_key_id
        });
        setSelectedLanguages([...selectedLanguages, language]);
        toast({
          title: "Language added",
          description: `${language.name} has been added to your languages.`
        });
      } catch (err) {
        console.error('Error adding language:', err);
        toast({
          title: "Error",
          description: "Failed to add language. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  // Handle removing a language
  const handleRemoveLanguage = async (languageId: string) => {
    try {
      await axios.delete(`/v2/user/languages/${languageId}`);
      setSelectedLanguages(selectedLanguages.filter(lang => lang.id !== languageId));
      toast({
        title: "Language removed",
        description: "The language has been removed from your selection."
      });
    } catch (err) {
      console.error('Error removing language:', err);
      toast({
        title: "Error",
        description: "Failed to remove language. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle playing a sample
  const handlePlaySample = (language: Language) => {
    // Set the audio source and play
    if (audioRef.current) {
      audioRef.current.src = language.url;
      audioRef.current.play().catch(e => {
        console.error("Audio playback failed:", e);
        toast({
          title: "Playback failed",
          description: "Audio sample couldn't be played. Please try again.",
          variant: "destructive"
        });
      });
    }
    
    toast({
      title: "Playing sample",
      description: `Playing a sample of ${language.name}.`
    });
  };

  // Calculate cost based on text length
  const [costPoints, setCostPoints] = useState<number | null>(null);
  useEffect(() => {
    if (!testText.trim()) {
      setCostPoints(null);
      return;
    }
    // debounce can be added
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      axios
        .post<{ points: number }>(`v2/voiceover/cost`, { text: testText })
        .then((r) => setCostPoints(r.data.points))
        .catch(() => setCostPoints(null));
    }, 1000); // 1000 мс = 1 секунда
  }, [testText]);

  // Handle generating speech
  const handleGenerateSpeech = async () => {
    if (!selectedTestLanguage) {
      toast({
        title: "No voices selected",
        description: "Please select a voice for speech generation.",
        variant: "destructive"
      });
      return;
    }
    
    if (!testText.trim()) {
      toast({
        title: "No text provided",
        description: "Please enter some text for speech generation.",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Generating speech",
      description: `Generating speech in ${selectedTestLanguage} voice for your text.`
    });
    
    // Find the language ID based on the selected name
    const selectedLangObject = selectedLanguages.find(lang => lang.name === selectedTestLanguage);
    if (!selectedLangObject) {
        toast({
            title: "Error",
            description: "Selected voice data not found.",
            variant: "destructive"
        });
        return;
    }
    const voiceId = selectedLangObject.id;

    try {
      const response = await axios.post('/v2/voiceover/generate', {
        voice_id: voiceId,
        text: testText
      });
      
      setGeneratedAudioSrc(response.data.audioUrl);
      toast({
        title: "Speech generated",
        description: `Your text has been converted to ${selectedTestLanguage} speech.`
      });
    } catch (err) {
      console.error('Error generating speech:', err);
      toast({
        title: "Generation failed",
        description: "Speech generation failed. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <Layout>
      <div className="container mx-auto py-10 space-y-12">
        {/* Hidden audio element for samples and generated audio */}
        <audio ref={audioRef} className="hidden" controls />
        
        {/* Languages List Section */}
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-3xl font-bold flex items-center gap-2">
              Voice Selection
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={18} className="text-muted-foreground cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    These 1-10 voices will appear later during checkout and in the API.  
                    You can update this list anytime.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </h2>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {selectedLanguages.length}/{maxLanguages} voices selected
              </span>
              <Input
                className="w-64"
                placeholder="Search voices or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {showInfo && (
            <Alert
              variant="default"
              className="bg-accent/10 border border-accent text-accent-foreground
                        overflow-hidden transition-[max-height,opacity] duration-500"
              style={{ maxHeight: showInfo ? 200 : 0 }}
            >
              <AlertTitle>Select up to 10 voices</AlertTitle>
              <AlertDescription>
                This list will be used for all future text-to-speech orders.  
                You can add or remove voices at any time.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Voice name</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead className="w-[100px]">Sample</TableHead>
                  <TableHead className="w-[100px]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        <span className="ml-2">Loading voices...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10 text-red-500">
                      {error}
                    </TableCell>
                  </TableRow>
                ) : languages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10">
                      No Voices found. Try a different search term.
                    </TableCell>
                  </TableRow>
                ) : (
                  languages.map((language) => (
                    <TableRow 
                      key={language.id} 
                      className="cursor-pointer hover:bg-primary/5"
                      onClick={() => handlePlaySample(language)}
                    >
                      <TableCell className="font-medium">{language.name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {language.tags.map((tag, idx) => (
                            <span 
                              key={idx} 
                              className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click from triggering
                            handlePlaySample(language);
                          }}
                        >
                          <Play size={16} />
                        </Button>
                      </TableCell>
                      <TableCell>
                        {selectedLanguages.some(lang => lang.id === language.id) ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click from triggering
                              handleRemoveLanguage(language.id);
                            }}
                          >
                            <X size={16} className="mr-1" /> Remove
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click from triggering
                              handleAddLanguage(language);
                            }}
                            disabled={selectedLanguages.length >= maxLanguages}
                          >
                            <Plus size={16} className="mr-1" /> Add
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => currentPage > 1 && setCurrentPage(p => p - 1)}
                  aria-disabled={currentPage === 1}
                />
              </PaginationItem>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => setCurrentPage(page)}
                    isActive={currentPage === page}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => currentPage < totalPages && setCurrentPage(p => p + 1)}
                  aria-disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
          
          {/* Selected Languages */}
          {selectedLanguages.length > 0 && (
            <div className="p-4 border rounded-lg bg-accent/10">
              <h3 className="text-lg font-semibold mb-2">My Selected Voices</h3>
              <div className="flex flex-wrap gap-2">
                {selectedLanguages.map(language => (
                  <div key={language.id} className="flex items-center gap-1 px-3 py-1 rounded-full border bg-background">
                    {language.name}
                    <button 
                      className="ml-1 text-muted-foreground hover:text-destructive" 
                      onClick={() => handleRemoveLanguage(language.id)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
        
        {/* Language Test Area */}
        <section className="space-y-6 border-t pt-10">
          <h2 className="text-3xl font-bold">Test a voice with your text</h2>
          
          <div className="grid grid-cols-1 gap-6 mx-auto max-w-3xl">
            <div className="space-y-2">
              <div className="flex justify-between">
                <label className="block text-sm font-medium">Text Input (1,000 character limit)</label>
                <span className="text-xs text-muted-foreground">{testText.length}/1000 characters</span>
              </div>
              <Textarea 
                placeholder="Enter your text here to test the speech generation..."
                value={testText}
                onChange={(e) => setTestText(e.target.value.slice(0, 1000))}
                className="min-h-[200px]"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <Select
                value={selectedTestLanguage}
                onValueChange={setSelectedTestLanguage}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select voice" />
                </SelectTrigger>
                <SelectContent>
                  {selectedLanguages.length > 0 ? (
                    selectedLanguages.map(lang => (
                      <SelectItem key={lang.id} value={lang.name}>
                        {lang.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>
                      No voices added
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              
              <Button 
                onClick={handleGenerateSpeech}
                disabled={!selectedTestLanguage || !testText.trim() || selectedLanguages.length === 0}
                className="flex-1"
              >
                Generate Speech
              </Button>
              
              <div className="text-sm">
                <span className="text-muted-foreground">Cost: </span>
                <span className={testText.length > 0 ? "font-medium" : "text-muted-foreground"}>
                  {costPoints !== null ? `${costPoints} pts` : '--'}
                </span>
              </div>
            </div>
            
            <div className="p-4 border rounded-lg bg-muted/10">
              <div className="text-sm text-muted-foreground mb-2">Preview will appear here after generation</div>
              {/* Audio player */}
              <div className="h-10 bg-muted/20 rounded flex items-center justify-center">
                <audio 
                  className="w-full" 
                  controls 
                  src={apiUrlForPath(generatedAudioSrc)}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default LanguagePage;